import React from 'react'

export interface BulkAction {
  label: string
  onClick: () => void
  variant?: 'default' | 'danger'
  disabled?: boolean
  icon?: React.ReactNode
}

/** Small inline icons for the common bulk actions (assign / delete). */
export const BulkIcons = {
  assign: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  ),
  delete: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  ),
}

/**
 * Compact inline action buttons shown in a list's top toolbar when one or more
 * rows are selected. Tiny by design: an icon plus a label that collapses to
 * icon-only on narrow widths. The parent owns selection + handlers.
 */
export const BulkActions: React.FC<{ actions: BulkAction[]; busy?: boolean }> = ({ actions, busy = false }) => (
  <div className="flex items-center gap-1.5">
    {actions.map((a) => (
      <button
        key={a.label}
        onClick={a.onClick}
        disabled={busy || a.disabled}
        title={a.label}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          a.variant === 'danger'
            ? 'text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/30'
            : 'text-txt-secondary bg-surface-secondary hover:bg-surface-hover border border-border/60'
        }`}
      >
        {a.icon}
        <span className="hidden sm:inline">{a.label}</span>
      </button>
    ))}
  </div>
)

/** A small, soft selection checkbox that stops click propagation so toggling a
 *  row never also triggers the row's onClick (navigation). */
export const SelectCheckbox: React.FC<{
  checked: boolean
  onChange: () => void
  title?: string
  indeterminate?: boolean
}> = ({ checked, onChange, title, indeterminate = false }) => {
  const ref = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked
  }, [indeterminate, checked])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      title={title}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation()
        onChange()
      }}
      className="w-4 h-4 rounded-md border border-border/50 text-brand-400 focus:ring-1 focus:ring-brand-400/30 cursor-pointer shrink-0 accent-brand-400"
    />
  )
}
